import React from 'react';
import { 
  Trophy, 
  Lock, 
  Heart, 
  Zap,
  Phone,
  ClipboardList,
  Search,
  FileCheck,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const whyChooseUs = [
  {
    icon: Trophy,
    title: 'Detectivi Bacau cu Experiență',
    description: 'Detectivii particulari Bacau au sute de cazuri de infidelitate rezolvate cu succes în județul Bacău.',
    color: 'amber'
  },
  {
    icon: Lock,
    title: 'Confidențialitate Absolută',
    description: 'Informațiile tale rămân strict între tine și echipa de detectivi privați Bacau.',
    color: 'blue'
  },
  {
    icon: Heart,
    title: 'Detectiv Bacau Empatic',
    description: 'Înțelegem că este o perioadă dificilă; detectivul particular Bacau acționează cu respect și profesionalism.',
    color: 'rose'
  },
  {
    icon: Zap,
    title: 'Răspuns Rapid în Bacău',
    description: 'Primele recomandări chiar în aceeași zi, investigații începând imediat în tot județul Bacău.',
    color: 'green'
  }
];

const processSteps = [
  {
    icon: Phone,
    step: '01',
    title: 'Contactează Detectivii Bacau',
    description: 'Apel sau mesaj confidențial pentru o evaluare rapidă a situației tale de către detectiv Bacau.'
  },
  {
    icon: ClipboardList,
    step: '02',
    title: 'Plan Personalizat',
    description: 'Detectivul particular Bacau stabilește obiective, buget și metodologia investigației împreună cu tine.'
  },
  {
    icon: Search,
    step: '03',
    title: 'Investigație Profesională',
    description: 'Detectivii privați Bacau efectuează supraveghere profesională, documentare și verificări complete.'
  },
  {
    icon: FileCheck,
    step: '04',
    title: 'Raport și Dovezi',
    description: 'Primești rapoarte clare, materiale multimedia și recomandări pentru pașii următori de la detectivii Bacau.'
  }
];

const colorClasses = {
  amber: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    icon: 'text-amber-400',
    gradient: 'from-amber-500 to-orange-500'
  },
  blue: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    icon: 'text-blue-400',
    gradient: 'from-blue-500 to-cyan-500'
  },
  rose: {
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/30',
    icon: 'text-rose-400',
    gradient: 'from-rose-500 to-pink-500'
  },
  green: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    icon: 'text-green-400',
    gradient: 'from-green-500 to-emerald-500'
  }
};

const WhyChooseSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Why Choose Us */}
        <div className="mb-24">
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 text-amber-400 font-semibold text-sm uppercase tracking-wider mb-4">
              <CheckCircle2 className="w-5 h-5" />
              De Ce Detectivi Bacau
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              De ce să alegi un <span className="text-amber-400">Detectiv Particular din Bacau?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              const colors = colorClasses[item.color];
              
              return (
                <div
                  key={index}
                  className={`group relative ${colors.bg} border ${colors.border} rounded-2xl p-6 
                    hover:bg-opacity-20 transition-all duration-300 hover:-translate-y-1`}
                >
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center mb-5
                    group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className={`w-7 h-7 ${colors.icon}`} />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-3">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-300 text-base leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* How It Works */}
        <div>
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 text-cyan-400 font-semibold text-sm uppercase tracking-wider mb-4">
              <ArrowRight className="w-5 h-5" />
              Procesul Nostru
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Cum <span className="text-cyan-400">funcționează</span>
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Un proces simplu și transparent, de la primul contact până la rezolvarea cazului.
            </p>
          </div>

          {/* Process Steps */}
          <div className="relative">
            {/* Connection Line - Desktop */}
            <div className="hidden lg:block absolute top-24 left-[12%] right-[12%] h-1 bg-gradient-to-r from-cyan-500/20 via-cyan-500/40 to-cyan-500/20 rounded-full" />
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {processSteps.map((step, index) => {
                const IconComponent = step.icon;
                
                return (
                  <div
                    key={index}
                    className="relative group"
                  >
                    {/* Step Number */}
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-cyan-500 rounded-full flex items-center justify-center z-10
                      shadow-lg shadow-cyan-500/30 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-slate-900 font-bold text-sm">{step.step}</span>
                    </div>

                    {/* Card */}
                    <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 h-full
                      hover:border-cyan-500/30 hover:bg-slate-800/70 transition-all duration-300">
                      
                      {/* Icon */}
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl 
                        flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="w-8 h-8 text-cyan-400" />
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                        {step.title}
                      </h3>

                      {/* Description */}
                      <p className="text-slate-400 text-base leading-relaxed group-hover:text-slate-300 transition-colors">
                        {step.description}
                      </p>
                    </div>

                    {/* Arrow - Mobile/Tablet */}
                    {index < processSteps.length - 1 && (
                      <div className="lg:hidden flex justify-center my-4">
                        <ArrowRight className="w-6 h-6 text-cyan-500/50 rotate-90 md:rotate-0" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <p className="text-slate-400 text-lg mb-6">
              Gata să contactezi un detectiv particular Bacau? Primul pas este un simplu apel sau mesaj.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+40725040500"
                className="inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-600 
                  text-slate-900 font-bold text-lg px-8 py-4 rounded-xl transition-colors shadow-lg shadow-cyan-500/25"
              >
                <Phone className="w-5 h-5" />
                Sună Detectivii Bacau: +40 725 040 500
              </a>
              <a 
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 
                  text-white font-semibold text-lg px-8 py-4 rounded-xl transition-colors"
              >
                Trimite Mesaj
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
